import { useEffect, useState } from "react";
import { playthroughBase } from "../lib/sections.js";

/**
 * The finalized section play-throughs of a client (Oct 20), for DSP pages.
 *
 * `statuses` is the profile's `section_playthroughs` ({section_type: status});
 * it is absent while the feature is switched off, and then this does nothing.
 * For each section that is "ready", fetches its scene index once to get the
 * total length and a poster. Returns {section_type: {durationMs, posterUrl,
 * posterFocus, base}} — only for sections that actually have scenes, so a
 * caller can treat "present" as "playable".
 */
export function useSectionPlaythroughs(clientId, statuses, live) {
  // `live` (Oct 28): sections with a finalized output caregivers can watch,
  // even while a new draft is being authored. Older servers don't send it.
  const ready = (Array.isArray(live) ? [...live]
    : Object.entries(statuses || {}).filter(([, st]) => st === "ready").map(([t]) => t)).sort();
  const key = ready.join(",");
  const [info, setInfo] = useState({});

  useEffect(() => {
    if (!clientId || !ready.length) { setInfo({}); return undefined; }
    let live = true;
    Promise.all(ready.map(async type => {
      const base = playthroughBase(clientId, type);
      try {
        const res = await fetch(`${base}/scenes`);
        if (!res.ok) return null;
        const scenes = (await res.json()).scenes || [];
        if (!scenes.length) return null;
        const first = scenes.find(s => s.poster_url) || scenes[0];
        return [type, {
          base,
          durationMs: scenes.reduce((a, s) => a + (s.duration_ms || 0), 0),
          posterUrl: first.poster_url || null,
          posterFocus: first.poster_focus || null,
          sceneCount: scenes.length,
        }];
      } catch { return null; }
    })).then(pairs => { if (live) setInfo(Object.fromEntries(pairs.filter(Boolean))); });
    return () => { live = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, key]);

  return info;
}
